import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

interface CreateOrgData {
  name: string;
  description: string;
}

interface CreateOrganizationModalProps {
  onClose: () => void;
  onCreate: (orgData: CreateOrgData) => Promise<boolean>;
}

/**
 * Modal component for creating new organizations
 */
const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({ onClose, onCreate }) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState<CreateOrgData>({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!formData.name) {
      alert('Please enter an organization name');
      return;
    }

    setLoading(true);
    const success = await onCreate(formData);
    setLoading(false);
    if (success) {
      onClose();
    }
  };

  return (
         <div className="fixed inset-0 bg-gray-800/80 dark:bg-gray-950/80 overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
                         <h3 className="text-2xl cgm-text-primary mb-2">
               Create New Organization
             </h3>
            <p className="cgm-text-secondary text-sm">
              Add a new organization to the system
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium cgm-text-secondary mb-1">
                Organization Name
              </label>
              <input
                id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter organization name"
              />
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium cgm-text-secondary mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter organization description"
                rows={3}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="cgm-button w-full mt-6 py-3 px-4 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                  Creating Organization...
                </>
              ) : (
                'Create Organization'
              )}
            </button>
          </form>

          {/* Navigation Links */}
          <div className="mt-6 space-y-4 text-center">
                         <button
               onClick={onClose}
               className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
             >
               Cancel
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganizationModal; 